import { memo, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import classNames from 'classnames';
import { visuallyHidden } from '@mui/utils';

import { PRODUCT_TUTORIALS } from 'common/constants';
import { CameraInsightsIcon } from 'components/Icons/CameraInsightsIcon';
import Information from 'components/Information';

import styles from './WaveInsightsGraphHeader.module.scss';

interface Props {
  className?: string;
  hasInsightsData: boolean;
  labsAndInfo?: Record<string, any> & { tooltip: string; articleId: string; tutorialId: string };
  showLegend?: boolean;
  title?: string;
  showSmartCamIcon?: boolean;
  isFullscreen?: boolean;
}

export const WaveInsightsGraphHeader = ({
  className,
  hasInsightsData,
  labsAndInfo,
  showLegend = true,
  title = 'Wave Timeline',
  showSmartCamIcon = false,
  isFullscreen,
}: Props) => {
  const graphLabsAndInfo = useMemo(
    () =>
      labsAndInfo || {
        articleId: PRODUCT_TUTORIALS.articles.waveTimeline.id,
        segmentProps: {
          modalType: 'tutorial',
          uiElement: 'wave-timeline',
        },
        tutorialId: PRODUCT_TUTORIALS.tutorials.smartCams.id,
        tooltip: PRODUCT_TUTORIALS.articles.waveTimeline.tooltip,
      },
    [labsAndInfo],
  );

  return (
    <Box
      className={classNames(
        styles.graphHeader,
        !hasInsightsData && styles.graphHeaderNoData,
        className,
      )}
      data-testid="wave-insights-graph-header"
    >
      <Box className={styles.graphHeaderLeft}>
        <Typography
          variant="callout1"
          component="h2"
          color="text.base"
          className={styles.graphHeaderLabel}
        >
          {title}
        </Typography>
        {showSmartCamIcon && <CameraInsightsIcon />}
      </Box>
      <Box className={styles.graphHeaderRight}>
        {showLegend && (
          <dl className={styles.graphHeaderLegend} data-testid="wave-insights-legend">
            <Typography component="dt" variant="caption2" color="text.secondary">
              Waves
            </Typography>
            <dd style={visuallyHidden}>
              Graphed bar representing all waves that correspond to the rewind video
            </dd>
            <Typography component="dt" variant="caption2" color="text.secondary">
              Surfed waves
            </Typography>
            <dd style={visuallyHidden}>
              Graphed bar representing all waves that were ridden in the rewind video
            </dd>
          </dl>
        )}
        {graphLabsAndInfo && (
          <Information
            className={classNames(styles.graphHeaderInfoButton, isFullscreen && styles.hide)}
            {...graphLabsAndInfo}
            data-testid="cam-rewinds-info-button"
          />
        )}
      </Box>
    </Box>
  );
};

export default memo(WaveInsightsGraphHeader);
